import { RefObject } from 'react';
import RichTextEditor from 'react-components/dialog-forms/RichTextEditor';
import NoteField, {
  NoteFieldProps,
  NoteFieldRef,
} from 'react-components/dialog-forms/NoteField';
import { Control, FormState } from 'react-hook-form';
import { ControlledAutocomplete } from '../Autocomplete/ControlledAutocomplete';
import { ControlledCheckbox } from '../Checkbox/ControlledCheckbox';
import { ControlledCheckboxGroup } from '../CheckboxGroup/ControlledCheckboxGroup';
import { ControlledDateField } from '../Date/ControlledDateField';
import { ControlledMultiSelect } from '../MultiSelect/ControlledMultiSelect';
import { ControlledRadioGroup } from '../RadioGroup/ControlledRadioGroup';
import { ControlledSearchBar } from '../SearchBar/ControlledSearchBar';
import { ControlledSelect } from '../Select/ControlledSelect';
import { ControlledTextField } from '../TextField/ControlledTextField';
import { FormInput } from './formBuilder.types';

export type FormInputBuilderProps<T> = {
  formInput: FormInput<T>;
  control: Control<T>;
  formState?: FormState<T>;
  disabled?: boolean;
};

export const FormInputBuilder = <T,>({
  formInput,
  control,
  formState,
  disabled,
}: FormInputBuilderProps<T>) => {
  const sharedProps = {
    control: control,
    label: formInput.label,
    name: formInput.name,
    errors: formState?.errors,
    disabled: disabled || formInput.disabled,
    trackingName: formInput.trackingName,
    sensitive: formInput.sensitive,
  };
  switch (formInput.input) {
    case 'text':
      return <ControlledTextField {...sharedProps} {...formInput.props} />;
    case 'number':
      return (
        <ControlledTextField
          isNumber={true}
          {...sharedProps}
          {...formInput.props}
        />
      );
    case 'phone':
      return (
        <ControlledTextField
          isPhoneNumber={true}
          {...sharedProps}
          {...formInput.props}
        />
      );
    case 'select':
      return (
        <ControlledSelect {...sharedProps} fullWidth {...formInput.props} />
      );
    case 'multiSelect':
      return <ControlledMultiSelect {...sharedProps} {...formInput.props} />;
    case 'checkboxGroup':
      return <ControlledCheckboxGroup {...sharedProps} {...formInput.props} />;
    case 'radioGroup':
      return <ControlledRadioGroup {...sharedProps} {...formInput.props} />;
    case 'checkbox':
      return <ControlledCheckbox {...sharedProps} {...formInput.props} />;
    case 'autocomplete':
      return <ControlledAutocomplete {...sharedProps} {...formInput.props} />;
    case 'date':
      return <ControlledDateField {...sharedProps} {...formInput.props} />;
    case 'richText':
      return (
        <RichTextEditor
          {...sharedProps}
          trackingName={formInput.trackingName}
          {...formInput.props}
        />
      );
    case 'noteField': {
      const { ref: noteFieldRef, ...noteFieldProps } = formInput.props ?? {};
      return (
        <NoteField
          ref={noteFieldRef as RefObject<NoteFieldRef>}
          control={sharedProps.control}
          name={sharedProps.name}
          trackingName={formInput.trackingName}
          {...(noteFieldProps as Omit<
            NoteFieldProps,
            'ref' | 'control' | 'name'
          >)}
        />
      );
    }
    case 'searchBar':
      return <ControlledSearchBar {...sharedProps} {...formInput.props} />;
    default:
      throw new Error(
        `Unsupported input type: ${
          (formInput as FormInput<T>).label
        }. Supported types are: text, checkbox, select, autocomplete, and date`
      );
  }
};
